import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { routes } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TooltipModule } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { ContractStatus } from '@ffd/api-client';
import { Dialog } from '@angular/cdk/dialog';
import { Observable } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { SiteSetupNavbarComponent } from '../../site-setup/components/site-setup-navbar/site-setup-navbar.component';
import { SiteSetupComponentService, StepNumberEnum } from '../../site-setup/site-setup.component.service';
import { LicenseAgreementFormComponent } from '../../license-agreement-form';
import { LicenseAgreementProceedInfoModalComponent } from '../../license-agreement-proceed-info-modal/license-agreement-proceed-info-modal.component';

@Component({
  selector: 'lib-software-license-agreement',
  imports: [
    CommonModule,
    SvgIconComponent,
    LicenseAgreementFormComponent,
    TranslateModule,
    SiteSetupNavbarComponent,
    TooltipModule,
    ReactiveFormsModule,
  ],
  templateUrl: './software-license-agreement.component.html',
  styleUrl: './software-license-agreement.component.scss',
})
export class SoftwareLicenseAgreementComponent implements OnInit {
  private readonly service = inject(SiteSetupComponentService);
  public vm = this.service.viewModel;
  public ContractStatus = ContractStatus;
  private readonly dialog = inject(Dialog);
  private readonly router = inject(Router);
  private readonly tagPusherService = inject(TagPusherService);

  public routes = routes;
  public StepNumberEnum = StepNumberEnum;

  protected readonly licenseAgreementForm = this.service.getLicenseAgreementForm();
  private readonly signerControl = this.licenseAgreementForm.controls.signer.controls;

  public isAuthorizedForm = new FormGroup({
    isAuthorized: new FormControl(false),
    isNotAuthorized: new FormControl(false),
  });

  public ngOnInit(): void {
    this.subscribeToFormChanges();

    // If contract doesn't exist, begin contract creation (skipped from overview step)
    if (!this.vm().contract?.data) {
      this.service.beginContract();
    }

    // Check if contract is signed and user is waitlisted - skip to pending installation
    const contract = this.vm().contract?.data;
    const siteInstallRequirements = this.vm().siteInstallRequirements?.data;

    if (
      contract?.contractStatus === ContractStatus.SIGNED &&
      siteInstallRequirements?.allowedToInstall === false &&
      siteInstallRequirements?.pmsAccepted === true
    ) {
      // Navigate to pending-installation for waitlisted users
      this.service.setActiveStep(StepNumberEnum.InstallationPending);
      this.router.navigate([routes.setup, routes.waitlistBlocker]);
      return;
    }

    // Automatically set to authorized (skip authorization check)
    this.isAuthorizedForm.controls.isAuthorized.setValue(true, { emitEvent: false });

    if (this.vm().contract?.data?.contractStatus === ContractStatus.INITIATED) {
      this.tagPusherService.pushTag({
        event: TagEvent.LicenseAgreementVisible,
        contract_id: this.vm().contract?.data?.id,
      });
    }
  }

  public subscribeToFormChanges(): void {
    this.isAuthorizedForm.controls.isAuthorized.valueChanges.subscribe((selected) => {
      if (selected) {
        this.signerControl.signatoryFirstName.setValue(this.vm().contract?.data?.ownerFirstName);
        this.signerControl.signatoryLastName.setValue(this.vm().contract?.data?.ownerLastName);

        this.tagPusherService.pushTag({
          event: TagEvent.LicenseAgreementAuthorizedPerson,
          contract_id: this.vm().contract?.data?.id,
        });
      }
      this.isAuthorizedForm.controls.isNotAuthorized.setValue(false, { emitEvent: false });
    });
    this.isAuthorizedForm.controls.isNotAuthorized.valueChanges.subscribe((selected) => {
      if (selected) {
        this.signerControl.signatoryFirstName.setValue('');
        this.signerControl.signatoryLastName.setValue('');

        this.tagPusherService.pushTag({
          event: TagEvent.LicenseAgreementUnauthorizedPerson,
          contract_id: this.vm().contract?.data?.id,
        });
      }
      this.isAuthorizedForm.controls.isAuthorized.setValue(false, { emitEvent: false });
    });
  }

  public onSubmitLicenseAgreement(): void {
    if (
      !this.licenseAgreementForm.valid ||
      !this.isAuthorizedForm.controls.isAuthorized.value ||
      this.vm().contract?.data?.contractStatus !== ContractStatus.INITIATED
    ) {
      this.licenseAgreementForm.updateValueAndValidity();
    }
    this.service.submitForm(!!this.isAuthorizedForm.controls.isAuthorized.value);
  }

  public onDownloadClick(): void {
    this.service.downloadSignedPdf();
  }

  public onNextClick(): void {
    const nextStep = this.service.determineNextStep();
    this.service.setContractActiveStep({ step: nextStep });
    this.service.setActiveStep(nextStep);

    if (nextStep === StepNumberEnum.InstallationPending) {
      this.router.navigate([routes.setup, routes.waitlistBlocker]);
    } else {
      this.router.navigate([routes.setup, routes.serverInstallation, routes.checklist]);
    }
  }

  public openInfoModal(): Observable<boolean | undefined> {
    return this.dialog.open<boolean>(LicenseAgreementProceedInfoModalComponent, {
      data: {},
      width: '391px',
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
    }).closed;
  }

  public onBack(): void {
    const newStepNumber = StepNumberEnum.SoftwareLicenseAgreement - 1;
    this.service.setActiveStep(newStepNumber);
    this.router.navigate([routes.site, routes.showcaseSiteId, routes.home]);
  }
}
